import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GitCompare, Loader2, CheckCircle2, ArrowRight, ArrowLeft, Table2, Link2, Sigma, FileText, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { DiffSection, AttrChangeCard } from '@/components/CompareDiff';
import { PromoteDiffButton } from '@/components/PromoteDiffButton';

export default function ModelComparePage() {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [leftId, setLeftId] = useState('');
  const [rightId, setRightId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fieldQuery, setFieldQuery] = useState('');

  // Table-or-field filter over the compare result: keep a diff row when the
  // table/measure name matches, or (for changed entities) any changed field name.
  const matchChange = (it, q) => {
    if ((it.name || '').toLowerCase().includes(q)) return true;
    return (it.changes || []).some((c) => {
      const names = [
        ...(c.attributes_added || []), ...(c.attributes_removed || []),
        ...((c.attributes_modified || []).map((m) => m.name)), c.field, c.name,
      ].filter(Boolean);
      return names.some((n) => String(n).toLowerCase().includes(q));
    });
  };
  const filterDiff = (diff, q) => {
    if (!diff || !q) return diff;
    return {
      ...diff,
      added: (diff.added || []).filter((it) => (it.name || '').toLowerCase().includes(q)),
      removed: (diff.removed || []).filter((it) => (it.name || '').toLowerCase().includes(q)),
      changed: (diff.changed || []).filter((it) => matchChange(it, q)),
      unchanged: 0,
    };
  };

  useEffect(() => {
    api.getSemanticModels().then(m => {
      setModels(m);
      if (m.length >= 2) { setLeftId(m[0].model_id); setRightId(m[1].model_id); }
      else if (m.length === 1) setLeftId(m[0].model_id);
    }).catch(e => toast.error(e.message));
  }, []);

  const runCompare = async () => {
    if (!leftId || !rightId) return toast.error('Select two models');
    if (leftId === rightId) return toast.error('Select two different models');
    setLoading(true); setResult(null);
    try { setResult(await api.compareSemanticModels(leftId, rightId)); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const nameOf = (id) => models.find(m => m.model_id === id)?.name || id;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="model-compare-page">
      <button onClick={() => navigate('/semantic-models')} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-models">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Models
      </button>
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 border border-primary/20"><GitCompare className="w-6 h-6 text-primary" /></div>
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Compare Models</h1>
          <p className="text-sm text-muted-foreground mt-1">See exactly what differs between two semantic models — entities, fields, relationships and measures.</p>
        </div>
      </div>

      <Card className="glass-card rounded-2xl border-border/50 p-5">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1 space-y-1.5">
            <label className="text-[11px] text-muted-foreground font-medium">Left model (baseline)</label>
            <Select value={leftId} onValueChange={setLeftId}>
              <SelectTrigger data-testid="compare-left-select"><SelectValue placeholder="Select model" /></SelectTrigger>
              <SelectContent>{models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <GitCompare className="w-5 h-5 text-muted-foreground hidden sm:block mb-2.5" />
          <div className="flex-1 space-y-1.5">
            <label className="text-[11px] text-muted-foreground font-medium">Right model (compare to)</label>
            <Select value={rightId} onValueChange={setRightId}>
              <SelectTrigger data-testid="compare-right-select"><SelectValue placeholder="Select model" /></SelectTrigger>
              <SelectContent>{models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={runCompare} disabled={loading} className="rounded-xl" data-testid="run-compare-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitCompare className="w-4 h-4 mr-2" />} Compare
          </Button>
        </div>
      </Card>

      {result && (
        <div className="space-y-4" data-testid="compare-result">
          <div className="flex items-center justify-between flex-wrap gap-2 px-1">
            <div className="text-sm text-muted-foreground">
              <span className="text-rose-400 font-medium">{nameOf(result.left.model_id)}</span>
              <ArrowRight className="w-3.5 h-3.5 inline mx-2" />
              <span className="text-emerald-400 font-medium">{nameOf(result.right.model_id)}</span>
            </div>
            {result.identical
              ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20" data-testid="identical-badge"><CheckCircle2 className="w-3 h-3 mr-1" /> Models are identical</Badge>
              : <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-amber-400 border-amber-500/30">Differences found</Badge>
                  <PromoteDiffButton resourceType="semantic_model" diff={result} disabled={result.identical} />
                </div>}
          </div>

          {!result.identical && (
            <div className="relative max-w-xs">
              <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={fieldQuery} onChange={(e) => setFieldQuery(e.target.value)}
                placeholder="Find table or field…" className="pl-9 h-8 text-xs rounded-lg" data-testid="compare-field-search" />
            </div>
          )}

          {result.model_attributes.length > 0 && (
            <AttrChangeCard title="Model Metadata" icon={FileText} changes={result.model_attributes} testid="diff-section-metadata" />
          )}

          <DiffSection title="Entities" icon={Table2} diff={filterDiff(result.entities, fieldQuery.trim().toLowerCase())} />
          <DiffSection title="Relationships" icon={Link2} diff={filterDiff(result.relationships, fieldQuery.trim().toLowerCase())} />
          <DiffSection title="Measures" icon={Sigma} diff={filterDiff(result.measures, fieldQuery.trim().toLowerCase())} />
        </div>
      )}
    </div>
  );
}
